import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { useForgotPassword, ForgotPasswordSteps } from "../hooks/useForgotPassword";
import { useAuth } from "../context/AuthContext";
import logoSvg from "../assets/UpdatedLogo/LOGO.svg";
import logoTextSvg from "../assets/updatedLogo.svg";
import emailIcon from "../assets/Login/email.svg";
import lockIcon from "../assets/Login/lock.svg";
import bgImage from "../assets/Login/login_Background.svg";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // Forgot password flow states and VM hook
  const [mode, setMode] = useState("login"); // "login" | "forgot"
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetConfirmPw, setShowResetConfirmPw] = useState(false);

  const forgotPassword = useForgotPassword(() => {
    // Redirect/switch back to login mode on successful reset
    setMode("login");
  });

  const handleLogin = async () => {
    setErr("");
    if (!email || !password) {
      setErr("Please enter both email and password.");
      return;
    }

    setLoading(true);
    const payload = { email, password };

    try {
      const response = await fetch("https://distinguished-robby-aagati-15819a4f.koyeb.app/api/v1/admin/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const resData = await response.json();

      if (!response.ok) {
        throw new Error(resData.message || resData.error || "Invalid email or password.");
      }

      localStorage.setItem("tokens", resData.data.access_token);
      localStorage.setItem("refresh tokens", resData.data.refresh_token);
      localStorage.setItem("expires in", resData.data.expires_in_seconds);
      localStorage.setItem("refresh expires", resData.data.refresh_expires_in_seconds);

      const userDetails = {
        id: resData.data.id,
        email: resData.data.email,
        display_name: resData.data.display_name,
        role: resData.data.role
      };

      localStorage.setItem("user_details", JSON.stringify(userDetails));

      const loggedInUser = {
        id: userDetails.id,
        name: userDetails.display_name || email.split("@")[0],
        email: userDetails.email || email,
        role: userDetails.role || "admin",
        avatar: userDetails.display_name ? userDetails.display_name.split(" ").map(n => n[0]).join("") : "A"
      };

      login(loggedInUser);
      if (loggedInUser.role === "reviewer") {
        navigate("/products", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      setErr(error.message || "Failed to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center relative bg-cover bg-center"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="w-full max-w-[520px] px-6 z-10 flex flex-col items-center">

        {/* Logo Section */}
        <div className="flex flex-col items-center mb-8">
          <img src={logoSvg} alt="Zytama Logo" className="h-24 w-auto mb-1" />
          <img src={logoTextSvg} alt="Zytama" className="h-9 w-auto mb-2" />
          <p className="text-xs font-bold text-gray-900 tracking-wide">
            Know exactly what your family is eating.
          </p>
        </div>

        {/* Welcome Text */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-extrabold text-black mb-2 tracking-tight">
            {mode === "login" ? "Hey, Welcome Back!" : (
              forgotPassword.step === ForgotPasswordSteps.EMAIL ? "Forgot Password?" :
              forgotPassword.step === ForgotPasswordSteps.OTP ? "Verify OTP" :
              forgotPassword.step === ForgotPasswordSteps.RESET ? "Reset Password" :
              "Success!"
            )}
          </h1>
          <p className="text-gray-500 font-medium text-[15px] max-w-[400px] mx-auto leading-relaxed">
            {mode === "login" ? "Sign in to your account" : (
              forgotPassword.step === ForgotPasswordSteps.EMAIL ? "Enter your registered email address to receive an OTP code" :
              forgotPassword.step === ForgotPasswordSteps.OTP ? `We've sent a 6-digit OTP code to ${forgotPassword.email}` :
              forgotPassword.step === ForgotPasswordSteps.RESET ? "Create and confirm your new secure password" :
              "Your password has been changed successfully!"
            )}
          </p>
        </div>

        {/* Login/Forgot Card */}
        <div className="bg-white rounded-[14px] shadow-[0px_0px_10px_0px_#0000001F] w-full p-8 border border-gray-100/50">

          {mode === "login" ? (
            <>
              {/* Email Field */}
              <div className="mb-5">
                <label className="block text-[15px] font-bold text-[#00000099] mb-2">Email</label>
                <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    placeholder="johnsondoe@nomail.com"
                    className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-[15px] bg-white placeholder:text-gray-400"
                  />
                  <div className="pr-4 flex items-center justify-center pointer-events-none">
                    <img src={emailIcon} alt="Email" className="w-5 h-5 opacity-70" />
                  </div>
                </div>
              </div>

              {/* Password Field */}
              <div className="mb-3">
                <label className="block text-[15px] font-bold text-[#00000099] mb-2">Password</label>
                <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    placeholder="••••••••••••••••"
                    className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-sm bg-white placeholder:text-gray-400 tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="pr-4 flex items-center justify-center cursor-pointer hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
                  >
                    <img src={lockIcon} alt="Lock" className={`w-5 h-5 ${showPw ? 'opacity-100' : 'opacity-70'}`} />
                  </button>
                </div>
              </div>

              {/* Forgot Password */}
              <div className="flex justify-end mb-8">
                <button
                  type="button"
                  onClick={() => setMode("forgot")}
                  className="text-[14px] text-blue-500 hover:text-blue-700 hover:underline transition-all underline decoration-blue-500/30 underline-offset-4"
                >
                  Forgot Password?
                </button>
              </div>

              {err && (
                <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 flex items-start">
                  {err}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full bg-[#0d5f66] hover:bg-[#0a4e54] text-white font-bold py-3.5 px-4 rounded-lg flex items-center justify-center transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative shadow-md"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span className="text-[16px] tracking-wide">Sign In</span>
                    <ArrowRight className="absolute right-4 w-6 h-6 opacity-90 group-hover:translate-x-1 transition-transform" strokeWidth={1} />
                  </>
                )}
              </button>
            </>
          ) : (
            <>


              {/* Forgot Password Errors */}
              {forgotPassword.error && (
                <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 flex items-start">
                  <span className="font-bold mr-2">!</span>
                  {forgotPassword.error}
                </div>
              )}

              {/* Step 1: Email Form */}
              {forgotPassword.step === ForgotPasswordSteps.EMAIL && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-[15px] font-bold text-[#00000099] mb-2">Email ID</label>
                    <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                      <input
                        type="email"
                        value={forgotPassword.email}
                        onChange={(e) => forgotPassword.setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && forgotPassword.handleRequestOtp()}
                        placeholder="johnsondoe@nomail.com"
                        className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-[15px] bg-white placeholder:text-gray-400"
                      />
                      <div className="pr-4 flex items-center justify-center pointer-events-none">
                        <img src={emailIcon} alt="Email" className="w-5 h-5 opacity-70" />
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={forgotPassword.handleRequestOtp}
                    disabled={forgotPassword.loading}
                    className="w-full bg-[#0d5f66] hover:bg-[#0a4e54] text-white font-bold py-3.5 px-4 rounded-lg flex items-center justify-center transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative shadow-md"
                  >
                    {forgotPassword.loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="text-[16px] tracking-wide">Verify</span>
                        <ArrowRight className="absolute right-4 w-6 h-6 opacity-90 group-hover:translate-x-1 transition-transform" strokeWidth={1} />
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Step 2: OTP Form */}
              {forgotPassword.step === ForgotPasswordSteps.OTP && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-[15px] font-bold text-[#00000099] mb-2">Verification Code (OTP)</label>
                    <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                      <input
                        type="text"
                        maxLength={6}
                        value={forgotPassword.otp}
                        onChange={(e) => forgotPassword.setOtp(e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => e.key === "Enter" && forgotPassword.handleVerifyOtp()}
                        placeholder="••••••"
                        className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-[18px] bg-white placeholder:text-gray-300 tracking-[0.3em] text-center font-semibold"
                      />
                    </div>
                  </div>

                  <button
                    onClick={forgotPassword.handleVerifyOtp}
                    disabled={forgotPassword.loading}
                    className="w-full bg-[#0d5f66] hover:bg-[#0a4e54] text-white font-bold py-3.5 px-4 rounded-lg flex items-center justify-center transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative shadow-md"
                  >
                    {forgotPassword.loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="text-[16px] tracking-wide">Verify OTP</span>
                        <ArrowRight className="absolute right-4 w-6 h-6 opacity-90 group-hover:translate-x-1 transition-transform" strokeWidth={1} />
                      </>
                    )}
                  </button>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={forgotPassword.handleRequestOtp}
                      className="text-xs font-bold text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      Resend Code
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Password Form */}
              {forgotPassword.step === ForgotPasswordSteps.RESET && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-[15px] font-bold text-[#00000099] mb-2">New Password</label>
                    <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                      <input
                        type={showResetPw ? "text" : "password"}
                        value={forgotPassword.password}
                        onChange={(e) => forgotPassword.setPassword(e.target.value)}
                        placeholder="••••••••••••••••"
                        className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-[15px] bg-white placeholder:text-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetPw(!showResetPw)}
                        className="pr-4 flex items-center justify-center cursor-pointer hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
                      >
                        <img src={lockIcon} alt="Lock" className={`w-5 h-5 ${showResetPw ? 'opacity-100' : 'opacity-70'}`} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-[15px] font-bold text-[#00000099] mb-2">Confirm Password</label>
                    <div className="relative flex items-center border border-[#00000059] rounded-[8px] bg-white transition-all">
                      <input
                        type={showResetConfirmPw ? "text" : "password"}
                        value={forgotPassword.confirmPassword}
                        onChange={(e) => forgotPassword.setConfirmPassword(e.target.value)}
                        placeholder="••••••••••••••••"
                        className="w-full py-3.5 px-4 rounded-[8px] outline-none text-gray-800 text-[15px] bg-white placeholder:text-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => setShowResetConfirmPw(!showResetConfirmPw)}
                        className="pr-4 flex items-center justify-center cursor-pointer hover:opacity-100 transition-opacity bg-transparent border-none outline-none"
                      >
                        <img src={lockIcon} alt="Lock" className={`w-5 h-5 ${showResetConfirmPw ? 'opacity-100' : 'opacity-70'}`} />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={forgotPassword.handleResetPassword}
                    disabled={forgotPassword.loading}
                    className="w-full bg-[#0d5f66] hover:bg-[#0a4e54] text-white font-bold py-3.5 px-4 rounded-lg flex items-center justify-center transition-all disabled:opacity-70 disabled:cursor-not-allowed group relative shadow-md"
                  >
                    {forgotPassword.loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <span className="text-[16px] tracking-wide">Change Password</span>
                        <ArrowRight className="absolute right-4 w-6 h-6 opacity-90 group-hover:translate-x-1 transition-transform" strokeWidth={1} />
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Step 4: Success Message */}
              {forgotPassword.step === ForgotPasswordSteps.SUCCESS && (
                <div className="flex flex-col items-center text-center py-6 space-y-4">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center text-green-600 shadow-inner">
                    <CheckCircle className="w-10 h-10" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-gray-900">Success!</h3>
                    <p className="text-[14px] text-gray-500 font-medium">Your password has been changed successfully.</p>
                  </div>
                  <p className="text-xs text-gray-400 animate-pulse font-medium">Redirecting you to login page...</p>
                </div>
              )}

              {/* Forgot Password Footer Options */}
              {forgotPassword.step !== ForgotPasswordSteps.SUCCESS && (
                <div className="mt-8 flex flex-col items-center space-y-4 border-t border-gray-100 pt-6">
                  {/* Back to Login Button */}
                  <button
                    onClick={() => {
                      forgotPassword.resetFlow();
                      setMode("login");
                    }}
                    className="flex items-center text-[14px] text-gray-500 hover:text-gray-800 transition-colors font-bold group"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform" />
                    Back to Sign In
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </div>
  );
}
